import { useState } from "react";
import { Plug, RefreshCw, Unplug } from "lucide-react";
import { Alert } from "@/components/common/Alert/Alert";
import { Button } from "@/components/shadcn/button";
import { ConfirmDialog } from "@/components/common/Dialog";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { disconnectCalendar, startCalendarConnect } from "../api";
import type { GraphConnection } from "../types";
import { AccountSettingsSectionHeading } from "./AccountSettingsSectionHeading";
import { ConnectionDetails, DetailItem } from "./ConnectionDetails";
import { ConnectionStatusBadge } from "./ConnectionStatusBadge";
import { SyncWindowCallout } from "./SyncWindowCallout";
import { CalendarSyncHelp } from "./CalendarSyncHelp";

type CalendarConnectionCardProps = {
  connection: GraphConnection;
  onChanged: () => Promise<void>;
};

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export const CalendarConnectionCard = ({ connection, onChanged }: CalendarConnectionCardProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const connect = async () => {
    setIsRedirecting(true);
    try {
      window.location.assign(await startCalendarConnect());
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to start calendar authorisation.");
      setIsRedirecting(false);
    }
  };

  const disconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectCalendar();
      showSuccessToast("Calendar disconnected.");
      setConfirmOpen(false);
      await onChanged();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to disconnect calendar.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const busy = isRedirecting || isDisconnecting;
  const hasSyncWindow = !!connection.deltaWindowStartUtc && !!connection.deltaWindowEndUtc;
  const isPaused = connection.isConnected && !connection.isEnabled;

  return (
    <div className="min-w-0">
      <AccountSettingsSectionHeading
        title="Calendar"
        subtitle="Two-way sync between your Microsoft calendar and your diary."
      />

      {connection.error && <Alert variant="error" persistent>{connection.error}</Alert>}

      <ConnectionDetails>
        <DetailItem label="Status">
          <ConnectionStatusBadge isConnected={connection.isConnected} isEnabled={connection.isEnabled} />
        </DetailItem>
        {connection.isConnected && (
          <>
            <DetailItem label="Microsoft account">{connection.email ?? "Not available"}</DetailItem>
            <DetailItem label="Connected">
              {connection.connectedDateUtc
                ? dateTimeFormatter.format(new Date(connection.connectedDateUtc))
                : "Not available"}
            </DetailItem>
          </>
        )}
      </ConnectionDetails>

      {isPaused && (
        <div className="mt-5">
          <Alert variant="warning" persistent>
            Syncing is paused because Microsoft stopped accepting your saved sign-in - usually
            because your password changed or access was withdrawn. Reconnect to resume: your
            existing sync dates are kept, so nothing is re-synced or lost.
          </Alert>
        </div>
      )}

      {connection.isConnected && !isPaused && (
        <div className="mt-5">
          {hasSyncWindow ? (
            <SyncWindowCallout
              start={connection.deltaWindowStartUtc}
              end={connection.deltaWindowEndUtc}
            />
          ) : (
            <Alert variant="information" persistent>
              Your calendar is connected. The range we watch for new appointments is set at the
              next sync.
            </Alert>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {connection.isConnected ? (
          <>
            <Button variant={isPaused ? "default" : "outline"} size="sm" onClick={connect} disabled={busy}>
              <RefreshCw className="size-4" aria-hidden />
              {isRedirecting
                ? "Redirecting..."
                : isPaused ? "Resume syncing" : "Reconnect calendar"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
              <Unplug className="size-4" aria-hidden />
              Disconnect calendar
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={connect} disabled={busy}>
            <Plug className="size-4" aria-hidden />
            {isRedirecting ? "Redirecting..." : "Connect calendar"}
          </Button>
        )}
      </div>

      <div className="mt-6">
        <p className="mb-1 text-sm font-semibold">What gets synced</p>
        <p className="my-1 text-sm text-muted-foreground">
          Once you connect your Microsoft calendar, Klipboard CRM keeps it in step with your
          diary automatically, in both directions.
        </p>
        <ul className="my-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Anything already syncing stays in sync for good, whatever date it's on.</li>
          <li>
            New appointments added in your calendar are picked up automatically if they're
            within about the next year. Appointments further ahead are picked up nearer the time.
          </li>
          <li>Your existing calendar history isn't copied in.</li>
          <li>
            Anything you create in Klipboard CRM is sent to your calendar at the next sync,
            whatever its date.
          </li>
        </ul>

        <div className="mt-4">
          <CalendarSyncHelp />
        </div>
      </div>

      <hr className="pb-4 mt-4" />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        hideTrigger
        title="Disconnect calendar?"
        description="Disconnecting resets your sync dates - reconnecting later starts from that point, so appointments from before it won't be picked up. Appointments already in your calendar are left in place."
        confirmText="Disconnect"
        loading={isDisconnecting}
        onConfirm={disconnect}
      />
    </div>
  );
};
